/** Minimum password length (align with PocketBase defaults where possible). */
export const PASSWORD_MIN_LENGTH = 8;

const socialMediaDomains = {
  figma: ["figma.com"],
  x: ["twitter.com", "x.com"],
  dribbble: ["dribbble.com"],
  behance: ["behance.net"],
  linkedin: ["linkedin.com"],
  instagram: ["instagram.com"],
  facebook: ["facebook.com", "fb.com"],
  tiktok: ["tiktok.com"],
  threads: ["threads.net"],
  youtube: ["youtube.com", "youtu.be"],
};

function isValidSocialMediaLink(url, platform) {
  if (!url) return true;
  if (!isValidURL(url)) return false;

  try {
    const urlObj = new URL(url);
    const domain = urlObj.hostname.toLowerCase();
    const validDomains = socialMediaDomains[platform];

    if (!validDomains) return true;

    return validDomains.some(
      (validDomain) =>
        domain === validDomain || domain.endsWith(`.${validDomain}`)
    );
  } catch {
    return false;
  }
}

function validateSocialVisible(data, visibleInputs) {
  const errors = {};
  Object.keys(socialMediaDomains).forEach((platform) => {
    if (visibleInputs?.[platform] && !data[platform]) {
      errors[platform] = `Please enter selected links`;
    }
    if (visibleInputs?.[platform] && data[platform]) {
      if (!isValidSocialMediaLink(data[platform], platform)) {
        errors[platform] = `Please enter a valid ${platform} URL`;
      }
    }
  });
  return errors;
}

export function isUsernameValid(username) {
  return /^[a-zA-Z0-9_]+$/.test(username);
}

export function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function isValidURL(url) {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Login modal: identity is email or username; password required.
 * Accepts `identity` or legacy `email` key for the first field.
 * @returns {{ errors: Record<string, string> }}
 */
export function validateLogin({ email, identity, password }) {
  const errors = {};
  const raw =
    typeof identity === "string"
      ? identity
      : typeof email === "string"
        ? email
        : "";
  const id = raw.trim();
  if (!id) errors.identity = "Email or username is required";
  if (!password) errors.password = "Password is required";
  return { errors };
}

/**
 * Forgot-password step: email only.
 */
export function validateForgotEmail(email) {
  const errors = {};
  const e = typeof email === "string" ? email.trim() : "";
  if (!e) errors.email = "Email is required";
  else if (!isValidEmail(e)) errors.email = "Invalid email address";
  return { errors };
}

/**
 * Reset password: new password strength + confirmation match.
 */
export function validateResetPassword({ password, confirmPassword }) {
  const errors = {};
  if (!password) errors.password = "Password is required";
  else if (password.length < PASSWORD_MIN_LENGTH) {
    errors.password = `Password must be at least ${PASSWORD_MIN_LENGTH} characters`;
  }
  if (!confirmPassword) errors.confirmPassword = "Please confirm your password";
  else if (password && confirmPassword !== password) {
    errors.confirmPassword = "Passwords do not match";
  }
  return { errors };
}

/**
 * Full signup page validation (avatar field `image`).
 */
export function validateSignupForm(data, lang = "en", visibleInputs = {}) {
  const errors = {};

  if (!data.image) {
    errors.image = "Please upload a profile image!";
  }

  if (!data.email) {
    errors.email = "Email is required";
  } else if (!isValidEmail(data.email)) {
    errors.email = "Invalid email address";
  }

  if (!data.password || data.password.length <= 5) {
    errors.password = "Password is required";
  }

  if (!data.username) {
    errors.username = "Username is required";
  } else if (data.username.length > 16 || data.username.length < 5) {
    errors.username = "Username must be between 5 and 16 characters";
  } else if (!isUsernameValid(data.username)) {
    errors.username =
      "Username can only contain letters, numbers, and underscores";
  }

  if (!data.fullName) {
    errors.fullName = "Full name is required";
  }

  if (
    !data.nationality ||
    data?.nationality?.replaceAll(" ", "")?.replace(/(\r\n|\n|\r)/gm, "") === ""
  ) {
    errors.nationality = "Nationality is required";
  }
  if (!data.subRole) {
    errors.subRole = "subRole is required";
  }
  if (
    data.subRole &&
    !["Student", "Others"].includes(data.subRole) &&
    !data.experience
  ) {
    errors.experience = "Experience is required";
  }

  if (!data.intro) {
    errors.intro = "Introduction is required";
  } else if (data.intro.length > 240 || data.intro.length < 30) {
    errors.intro = "Your introduction should be 30 to 240 characters long.";
  }

  if (!data.terms) {
    errors.terms =
      lang === "en"
        ? "You should accept the terms of use"
        : "利用規約に同意する必要です";
  }

  if (data?.personalSite && !isValidURL(data?.personalSite)) {
    errors.personalSite = "Enter a valid URL";
  }

  if (data?.personalBlog && !isValidURL(data?.personalBlog)) {
    errors.personalBlog = "Enter a valid URL";
  }

  Object.assign(errors, validateSocialVisible(data, visibleInputs));

  return errors;
}

/**
 * Settings profile: uses `profile_img` instead of signup `image`.
 */
export function validateProfileForm(data, lang = "en", visibleInputs = {}) {
  const errors = {};

  if (!data.profile_img) {
    errors.profile_img =
      lang === "en"
        ? "Please upload a profile image!"
        : "プロフィール画像をアップロードしてください。";
  }

  if (!data.username) {
    errors.username = "Username is required";
  } else if (data.username.length > 16 || data.username.length < 5) {
    errors.username = "Username must be between 5 and 16 characters";
  } else if (!isUsernameValid(data.username)) {
    errors.username =
      "Username can only contain letters, numbers, and underscores";
  }

  if (!data.fullName) {
    errors.fullName = "Full name is required";
  }

  if (
    !data.nationality ||
    data?.nationality?.replaceAll(" ", "")?.replace(/(\r\n|\n|\r)/gm, "") === ""
  ) {
    errors.nationality = "Nationality is required";
  }

  if (!data.subRole) {
    errors.subRole = "Role is required";
  }
  if (
    data.subRole &&
    String(data.subRole).trim().toLowerCase() !== "student" &&
    !data.experience
  ) {
    errors.experience = "Experience is required";
  }

  if (!data.intro) {
    errors.intro = "Introduction is required";
  } else if (data.intro.length > 240 || data.intro.length < 30) {
    errors.intro = "Your introduction should be 30 to 240 characters long.";
  }

  if (data?.personalSite && !isValidURL(data?.personalSite)) {
    errors.personalSite = "Enter a valid URL";
  }

  if (data?.personalBlog && !isValidURL(data?.personalBlog)) {
    errors.personalBlog = "Enter a valid URL";
  }

  Object.assign(errors, validateSocialVisible(data, visibleInputs));

  return errors;
}

/** @deprecated Use validateSignupForm */
export const valIDateForm = validateSignupForm;
